import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import FormHelperText from '@material-ui/core/FormHelperText'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import * as Cognito from '../../lib/cognito-api'
import { reportError } from '../../lib/raygun'
import PasswordTextField from './password-text-field'
import { formSchema } from './validation'
import { useStyles } from './styles'
import Link from '@material-ui/core/Link'

const today = new Date()
const ageCutoff = new Date(today.getFullYear() - 13, today.getMonth(), today.getDate())

const SignUpForm = ({ onSuccess }) => {
  const classes = useStyles()
  const [submissionError, setSubmissionError] = useState(null)

  const { register, handleSubmit, errors, formState } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(formSchema),
  })
  const { isSubmitting } = formState

  const accountExistsErrorLink = (username, error) => {
    return (
      <>
        <FormHelperText error={true}>{error.message}</FormHelperText>
        <Link style={{ textAlign: 'center' }} component={RouterLink} to={`/login?username=${encodeURIComponent(username)}`}>
          Click here to log in.
        </Link>
      </>
    )
  }

  const onFormSubmit = async (formValues) => {
    setSubmissionError(null)

    if (formValues.birthdate > ageCutoff) {
      setSubmissionError(<FormHelperText error={true}>Unable to create account at this time.</FormHelperText>)
      return
    }

    try {
      await Cognito.signUp({
        email: formValues.email.trim(),
        password: formValues.password,
        firstName: formValues.firstName.trim(),
        lastName: formValues.lastName.trim(),
        birthdate: formValues.birthdate.toISOString().slice(0, 10),
      })
      onSuccess && onSuccess(formValues)
    } catch (error) {
      switch (error.code) {
        case 'InvalidPasswordException':
        case 'UsernameExistsException':
          setSubmissionError(accountExistsErrorLink(formValues.email.trim(), error))
          break
        default:
          reportError(error)
          setSubmissionError(<FormHelperText error={true}>An unexpected error occurred. Please try again.</FormHelperText>)
          break
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <TextField
        className={classes.formField}
        name="firstName"
        label="First name"
        inputRef={register}
        error={!!errors.firstName}
        helperText={errors.firstName && errors.firstName.message}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        className={classes.formField}
        name="lastName"
        label="Last name"
        inputRef={register}
        error={!!errors.lastName}
        helperText={errors.lastName && errors.lastName.message}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        className={classes.formField}
        name="email"
        label="Email"
        inputRef={register}
        error={!!errors.email}
        helperText={errors.email && errors.email.message}
        InputLabelProps={{ shrink: true }}
      />
      <PasswordTextField
        className={classes.formField}
        name="password"
        label="Password"
        inputRef={register}
        error={!!errors.password}
        helperText={errors.password && errors.password.message}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        className={classes.formField}
        name="birthdate"
        label="Account Owner Date Of Birth"
        type="date"
        inputRef={register}
        error={!!errors.birthdate}
        helperText={errors.birthdate && errors.birthdate.message}
        InputLabelProps={{ shrink: true }}
      />
      <Box mb={5}>
        <FormControlLabel
          control={<Checkbox color="primary" inputRef={register} name="agreeToTOS" />}
          label={(
            <Typography variant="body2" className={classes.agreeToTOSLabel}>
              By checking this box, you are agreeing to PGA of America's&nbsp;
              <a href="https://www.pga.com/pga-of-america/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
              &nbsp;and&nbsp;
              <a href="https://www.pga.com/pga-of-america/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a>.
            </Typography>
          )}
          style={{ alignItems: 'start' }}
        />
        {errors.agreeToTOS && (
          <FormHelperText error={!!errors.agreeToTOS}>{errors.agreeToTOS.message}</FormHelperText>
        )}
      </Box>
      <Button
        fullWidth
        size="large"
        variant="contained"
        color="primary"
        type="submit"
        disabled={isSubmitting}
      >
        Create Account
      </Button>
      {submissionError}
    </form>
  )
}

export default SignUpForm
